import { number, object, string, boolean } from 'yup'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

export interface DefaultValues {
  guid: string
  start: ImmutableVector3
  end: ImmutableVector3
  isIntersecting: boolean
  label?: string
}

const getPoints = (values: DefaultValues) => ({
  startRounded: values.start,
  endRounded: values.end,
})

// Reusable validation functions
const createXYCartesianTest = () =>
  boolean().test({
    name: 'is-x-or-y-cartesian-purlin',
    message:
      'Pfette muss kartesisch in x oder y im Raum liegen. Das bedeutet, dass der x-Wert derselbe für Start und Ende ist oder der y-Wert derselbe für Start und Ende ist.',
    test: function () {
      const { startRounded, endRounded } = getPoints(this.parent as DefaultValues)
      return startRounded.x === endRounded.x || startRounded.y === endRounded.y
    },
  })

const createXYDiagonalTest = () =>
  boolean().test({
    name: 'is-xy-diagonal-purlin',
    message:
      'Pfette muss in x und y diagonal im Raum liegen. Das bedeutet, dass der x-Wert UND der y-Wert unterschiedlich für Start und Ende sind.',
    test: function () {
      const { startRounded, endRounded } = getPoints(this.parent as DefaultValues)
      return startRounded.x !== endRounded.x && startRounded.y !== endRounded.y
    },
  })

const createHorizontalTest = () =>
  boolean().test({
    name: 'is-horizontal-purlin',
    message:
      'Pfette muss horizontal im Raum liegen. Das bedeutet, dass der z-Wert derselbe für Start und Ende ist.',
    test: function () {
      const { startRounded, endRounded } = getPoints(this.parent as DefaultValues)
      return startRounded.z === endRounded.z
    },
  })

const createZElevatedTest = () =>
  boolean().test({
    name: 'is-z-elevated-purlin',
    message:
      'Pfette muss in z geneigt sein. Das bedeutet, dass der z-Wert unterschiedlich für Start und Ende ist.',
    test: function () {
      const { startRounded, endRounded } = getPoints(this.parent as DefaultValues)
      return startRounded.z !== endRounded.z
    },
  })

// Schema for X or Y purlins (horizontal)
export const xyCartesianHorizontalPurlinSchema = object({
  guid: string(),
  start: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  end: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  isXOrYCartesian: createXYCartesianTest(),
  isHorizontal: createHorizontalTest(),
})

// Schema for X/Y diagonal purlins
export const xyDiagonalHorizontalPurlinSchema = object({
  guid: string(),
  start: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  end: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  isXYDiagonal: createXYDiagonalTest(),
  isHorizontal: createHorizontalTest(),
})

// Schema for X or Y purlins (strictly x with z elevation)
export const xyCartesianZElevatedPurlinSchema = object({
  guid: string(),
  start: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  end: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  isXOrYCartesian: createXYCartesianTest(),
  isZElevated: createZElevatedTest(),
})

// Schema for XZY diagonal purlins
export const xyDiagonalZElevatedPurlinSchema = object({
  guid: string(),
  start: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  end: object({
    x: number(),
    y: number(),
    z: number(),
  }),
  isXYDiagonal: createXYDiagonalTest(),
  isZElevated: createZElevatedTest(),
})
